import { AvoidLib, PolyLine, Rectangle, ShapeRef } from "libavoid-js";
import { g, util, mvc, dia } from "@joint/core";
import { Region } from "../../mvc/region";
import { NodeLabel } from "../../mvc/base/labeled-node";
import { Statechart } from "../../mvc/statechart";
import { PreferenceStore } from "../preference-store";
import { EdgeLabelPlacer } from "./edge-label-placer";
import { Synchronization } from "../../mvc/synchronization";
import { getConfig } from "../../../../config";
import RappidContext from "../../rappid/rappid-context";
import {
  guardAvoidCall,
  onConfigureRouter,
  onRebuildRouter,
  withRouter,
  isAvoidAlive,
  invalidateRouterNow,
  getRouter,
} from "./avoid-loader";

// Weird: Each Avoid shape MUST have a Pin with classId of 1
// Depending on the node type, we apply different Pins.
// We need to make sure that in each case there is one Pin with classId 1 and avoid duplicate class IDs.
// This is done in this.updateShape
const centerPinId = 1;
const selfLinkInPinId = 8;
const selfLinkOutPinId = 9;
const topPinId = 1;
const rightPinId = 2;
const bottomPinId = 3;
const leftPinId = 4;
const synchInPinId = 1;
const synchOutPinId = 2;

const { layoutEnabled } = getConfig();

const shapeBufferDistance = 10;
const idealNudgingDistance = 10;
export class AvoidService {
  graph: dia.Graph;
  mode: "normal" | "silenced" | "forced" = "normal";
  edgeLabelPlacer: EdgeLabelPlacer;
  connDirections: Record<string, number>;
  linksByPointer: Record<number, dia.Link> = {};
  commitTransactions: boolean;
  cachedRoutes: Map<string | number, { arr: Float64Array; hash: number }> = new Map();
  margin: number;
  portOverflow: number;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  shapeRefs: Record<string, any> = {};
  edgeRefs: Record<string, any> = {};
  avoidConnectorCallback: (connRefPtr: any) => void;
  /* eslint-enable */
  context: RappidContext;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(context: RappidContext, options: Record<string, any> = {}) {
    this.context = context;
    this.graph = context.graph;
    this.edgeLabelPlacer = new EdgeLabelPlacer(this.context);
    guardAvoidCall((Avoid) => {
      this.connDirections = {
        top: Avoid.ConnDirUp,
        right: Avoid.ConnDirRight,
        bottom: Avoid.ConnDirDown,
        left: Avoid.ConnDirLeft,
        all: Avoid.ConnDirAll,
      };
    });
    this.avoidConnectorCallback = this.onAvoidConnectorChange.bind(this);
    this.commitTransactions = options.commitTransactions ?? true;
    this.cachedRoutes = new Map();
    this.configureRouterLoader();
  }

  destroy() {
    Object.keys(this.edgeRefs).forEach((id) => this.deleteConnector(id));
    Object.keys(this.shapeRefs).forEach((id) => this.deleteShape(id));
    this.shapeRefs = {};
    this.edgeRefs = {};
    this.linksByPointer = {};
    this.cachedRoutes.clear();
    invalidateRouterNow();
  }

  /** Libavoid docu is here: https://www.adaptagrams.org/documentation/namespaceAvoid.html */
  private configureRouterLoader() {
    // Configuration is done in avoid-loader
    // We're just setting the callbacks here
    onConfigureRouter((Avoid, router) => {
      router.setRoutingParameter(Avoid.idealNudgingDistance, idealNudgingDistance);
      router.setRoutingParameter(Avoid.shapeBufferDistance, shapeBufferDistance);
      // Setting a crossingPenalty makes the routing really slow.
      // In addition, libavoid docu states it is "still experimental! It is not recommended for normal use"
      //   router.setRoutingParameter(Avoid.crossingPenalty, 50);
      router.setRoutingOption(Avoid.nudgeOrthogonalTouchingColinearSegments, true);
      router.setRoutingOption(Avoid.performUnifyingNudgingPreprocessingStep, true);
      router.setRoutingOption(Avoid.nudgeSharedPathsWithCommonEndPoint, true);
      router.setRoutingOption(Avoid.nudgeOrthogonalSegmentsConnectedToShapes, true);
    });
    onRebuildRouter((Avoid, router) => {
      this.destroy();
    });
  }

  private getDirectionalPinId(sourceCenter: g.Point, targetCenter: g.Point): number {
    const dx = targetCenter.x - sourceCenter.x;
    const dy = targetCenter.y - sourceCenter.y;

    if (Math.abs(dx) > Math.abs(dy)) {
      return dx > 0 ? rightPinId : leftPinId;
    } else {
      return dy > 0 ? bottomPinId : topPinId;
    }
  }

  getAvoidRectFromElement(element: dia.Element): Rectangle {
    return guardAvoidCall((Avoid) => {
      const { x, y, width, height } = element.getBBox();
      const c = new Avoid.Point(x + width / 2, y + height / 2);
      const r = new Avoid.Rectangle(c, width, height);
      Avoid.destroy?.(c);
      return r;
    });
  }

  getVerticesFromAvoidRoute(sourceElement: dia.Element, targetElement: dia.Element, route: PolyLine) {
    const vertices = [];
    guardAvoidCall((Avoid) => {
      for (let i = 1; i < route.size() - 1; i++) {
        const p = route.get_ps(i); // Avoid.Point (Wrapper)
        const x = p.x,
          y = p.y;
        if (sourceElement.getBBox().containsPoint({ x, y }) || targetElement.getBBox().containsPoint({ x, y })) {
          // We must not add vertices that are inside the source or target element.
          // This is because JointJS will route the link through that vertex from/to the center of the element
          continue;
        }
        vertices.push({ x, y });
      }
    });
    return vertices;
  }

  updateShape(element: dia.Element) {
    if (element instanceof Region || element instanceof NodeLabel || element instanceof Statechart) {
      return;
    }
    withRouter((Avoid, avoidRouter) => {
      const shapeRect = this.getAvoidRectFromElement(element);
      if (this.shapeRefs[element.id]) {
        // Only update the position and size of the shape.
        const shapeRef = this.shapeRefs[element.id];
        avoidRouter.moveShape(shapeRef, shapeRect);
        return;
      }
      const shapeRef = new Avoid.ShapeRef(avoidRouter, shapeRect);
      this.shapeRefs[element.id] = shapeRef;
      Avoid.destroy?.(shapeRect);

      if (element instanceof Synchronization) {
        const bbox = element.getBBox();
        const isHorizontal = bbox.width > bbox.height;
        this.applySynchronizationPins(isHorizontal, shapeRef);
      } else if (this.hasEmbeddedRegions(element)) {
        // We must not add a center PIN for nodes with embedded regions
        // Reason: LibAvoid will route the edge to the center of the shapt
        // and tries to avoid the "inner" shapes of the node
        this.applyUpDownLeftRightPins(shapeRef);
        this.applySelfTransitionPINs(shapeRef);
      } else {
        this.applyCenterPin(shapeRef);
        this.applySelfTransitionPINs(shapeRef);
      }
    });
  }

  private createPin(
    shapeRef: ShapeRef,
    pinId: number,
    xOffest: number,
    yOffset: number,
    direction: number,
    proportional = true,
    offset = 0,
    exclusive = false
  ) {
    return guardAvoidCall((Avoid) => {
      const pin = new Avoid.ShapeConnectionPin(shapeRef, pinId, xOffest, yOffset, proportional, offset, direction);
      pin.setExclusive(exclusive);
      return pin;
    });
  }

  applySynchronizationPins(isHorizontal: boolean, shapeRef: ShapeRef) {
    if (isHorizontal) {
      this.createPin(shapeRef, synchInPinId, 0.5, 0, this.connDirections.top);
      this.createPin(shapeRef, synchOutPinId, 0.5, 1, this.connDirections.bottom);
    } else {
      this.createPin(shapeRef, synchInPinId, 0, 0.5, this.connDirections.left);
      this.createPin(shapeRef, synchOutPinId, 1, 0.5, this.connDirections.right);
    }
  }

  applyUpDownLeftRightPins(shapeRef: ShapeRef) {
    this.createPin(shapeRef, topPinId, 0.5, 0, this.connDirections.top);
    this.createPin(shapeRef, rightPinId, 1, 0.5, this.connDirections.right);
    this.createPin(shapeRef, bottomPinId, 0.5, 1, this.connDirections.bottom);
    this.createPin(shapeRef, leftPinId, 0, 0.5, this.connDirections.left);
  }

  applyCenterPin(shapeRef: ShapeRef) {
    this.createPin(shapeRef, centerPinId, 0.5, 0.5, this.connDirections.all, true, 0, false);
  }

  applySelfTransitionPINs(shapeRef: ShapeRef) {
    this.createPin(shapeRef, selfLinkInPinId, 0.6, 0, this.connDirections.top, true, 0, false);
    this.createPin(shapeRef, selfLinkOutPinId, 1, 0.4, this.connDirections.right, true, 0, false);
  }

  updateConnector(link: dia.Link) {
    return withRouter((Avoid, avoidRouter) => {
      const { shapeRefs, edgeRefs } = this;
      const { id: sourceId } = link.source();
      const { id: targetId } = link.target();

      if (!sourceId || !targetId) {
        // Should not happen ... this is copy+paste from jointjs example.
        this.deleteConnector(link.id);
        return null;
      }

      const isSelfTransition = sourceId === targetId;
      const sourceElement = link.getSourceElement();
      const targetElement = link.getTargetElement();
      const sourceHasEmbeddedRegion = this.hasEmbeddedRegions(sourceElement);
      const targetHasEmbeddedRegion = this.hasEmbeddedRegions(targetElement);
      const sourceIsSýnchronization = sourceElement instanceof Synchronization;
      const targetIsSýnchronization = targetElement instanceof Synchronization;

      let sourcePinId, targetPinId;

      if (isSelfTransition) {
        sourcePinId = selfLinkOutPinId;
        targetPinId = selfLinkInPinId;
      } else {
        if (sourceIsSýnchronization) {
          sourcePinId = synchOutPinId;
        } else if (!sourceHasEmbeddedRegion) {
          sourcePinId = centerPinId;
        } else {
          sourcePinId = this.getDirectionalPinId(sourceElement.getBBox().center(), targetElement.getBBox().center());
        }

        if (targetIsSýnchronization) {
          targetPinId = synchInPinId;
        } else if (!targetHasEmbeddedRegion) {
          targetPinId = centerPinId;
        } else {
          targetPinId = this.getDirectionalPinId(targetElement.getBBox().center(), sourceElement.getBBox().center());
        }
      }
      const sourceConnEnd = new Avoid.ConnEnd(shapeRefs[sourceId], sourcePinId);
      const targetConnEnd = new Avoid.ConnEnd(shapeRefs[targetId], targetPinId);
      let connRef;

      if (edgeRefs[link.id]) {
        connRef = edgeRefs[link.id];
      } else {
        connRef = new Avoid.ConnRef(avoidRouter);
        this.linksByPointer[Avoid.getPointer(connRef)] = link;
      }

      connRef.setSourceEndpoint(sourceConnEnd);
      connRef.setDestEndpoint(targetConnEnd);
      Avoid.destroy?.(sourceConnEnd);
      Avoid.destroy?.(targetConnEnd);

      if (edgeRefs[link.id]) {
        return connRef;
      }
      edgeRefs[link.id] = connRef;
      // Note: The next line is used quite often.
      // If you debug an error to be raised by this line, it is
      // most likely not caused, but only revelead by this line.
      // I am afraid you need to dig deeper :-(
      connRef.setCallback(this.avoidConnectorCallback, connRef);
      return connRef;
    });
  }

  hasEmbeddedRegions(cell: dia.Cell) {
    const embeddedCells = cell.getEmbeddedCells();
    for (let i = 0; i < embeddedCells.length; i++) {
      if (embeddedCells[i] instanceof Region) return true;
    }
    return false;
  }

  deleteConnector(linkId: string | number) {
    const connRef = this.edgeRefs[linkId];
    delete this.edgeRefs[linkId];
    this.cachedRoutes.delete(linkId);
    if (!connRef) return;
    withRouter((Avoid, avoidRouter) => {
      avoidRouter.deleteConnector(connRef);
      delete this.linksByPointer[Avoid.getPointer(connRef)];
    });
  }

  deleteShape(elementId: string | number) {
    const shapeRef = this.shapeRefs[elementId];
    if (!shapeRef) return;
    withRouter((Avoid, avoidRouter) => {
      avoidRouter.deleteShape(shapeRef);
    });
    delete this.shapeRefs[elementId];
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getLinkAnchorDelta(element: any, point: g.Point) {
    const bbox = element?.getBBox();
    if (!bbox) {
      return 0;
    }
    return point.difference(bbox.center());
  }

  // This is triggered by the avoidRouter
  // It takes the libavoid route and updates the JointJS link.
  routeLink(link: dia.Link) {
    const connRef = this.edgeRefs[link.id];
    if (!connRef) return;
    let route: PolyLine, sourcePoint, targetPoint, sourceId, targetId;
    let sourceElement, targetElement, sourceAnchorDelta, targetAnchorDelta;
    guardAvoidCall((Avoid) => {
      route = connRef.displayRoute();
      const { arr, hash } = this.extractAndHash(route);
      const prev = this.cachedRoutes?.get(link.id);
      if (prev && prev.hash === hash && this.sameRoute(prev.arr, arr)) {
        // unchanged → skip expensive work
        return;
      } else {
        this.cachedRoutes.set(link.id, { arr, hash }); // store the immutable copy
      }
      const p0 = route.get_ps(0);
      const pN = route.get_ps(route.size() - 1);
      sourcePoint = new g.Point({ x: p0.x, y: p0.y });
      targetPoint = new g.Point({ x: pN.x, y: pN.y });

      // Uncomment this if you want to validate the robustness of the avoid-loader
      // TestCase 1: Give any transition a spec of 666, do layout
      // Assert: It fails
      // Assert: Layout is undone in a clean way, you still can work with the give statechart
      // TestCase 2: Remove  666 from transition, do layout
      // Assert: Works
      // const spec = (link as Transition).getSpecification();
      // if (spec.includes("666")) {
      //   Avoid.destroy(spec);
      // }

      ({ id: sourceId } = link.source());
      ({ id: targetId } = link.target());

      sourceElement = link.getSourceElement();
      targetElement = link.getTargetElement();
      if (!sourceId || !targetId || !sourceElement || !targetElement) {
        throw new Error(
          "Invalid link: missing source or target, source was " + sourceElement + ", target was " + targetElement
        );
      }
      sourceAnchorDelta = this.getLinkAnchorDelta(sourceElement, sourcePoint);
      targetAnchorDelta = this.getLinkAnchorDelta(targetElement, targetPoint);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const linkAttributes: Record<string, any> = {
        source: {
          id: sourceId,
          port: null,
          anchor: {
            name: "modelCenter",
          },
        },
        target: {
          id: targetId,
          port: null,
          anchor: {
            name: "modelCenter",
          },
        },
      };

      if (this.isRouteValid(route)) {
        linkAttributes.source.anchor.args = {
          dx: sourceAnchorDelta.x,
          dy: sourceAnchorDelta.y,
        };
        linkAttributes.target.anchor.args = {
          dx: targetAnchorDelta.x,
          dy: targetAnchorDelta.y,
        };
        linkAttributes.vertices = this.getVerticesFromAvoidRoute(sourceElement, targetElement, route);
        this.edgeLabelPlacer.placeLabel(link);
        link.set(linkAttributes, { avoidRouter: true });
      } else {
        this.resetLink(link);
      }
    });
  }

  /**
   * Computes a rolling FNV-1a 32-bit hash of the route's quantized coordinates.
   * For performance reasons, it also collects/returns the flat coordinate,
   * so we dont need to re-iterates when taking over the route.
   *
   * @returns {{ arr: Float64Array, hash: number }} An object containing:
   *   - arr: Flat array of coordinates [x0, y0, x1, y1, ...].
   *   - hash: 32-bit unsigned integer hash of the quantized coordinates.
   */
  extractAndHash(route: PolyLine): { arr: Float64Array; hash: number } {
    // Adapt to your real data shape; the key is: flat numeric array + rolling hash

    const N = route.size() * 2;
    const buf = new Float64Array(N);
    const SCALE = 100; //  0.01 pixel precision

    // FNV-1a 32-bit
    let h = 0x811c9dc5 | 0;

    let k = 0;
    for (let i = 0; i < route.size(); i++) {
      const x = route.get_ps(i).x;
      const y = route.get_ps(i).y;
      buf[k++] = x;
      buf[k++] = y;

      // quantize then fold into hash (little-endian)
      const qx = Math.round(x * SCALE) | 0;
      const qy = Math.round(y * SCALE) | 0;

      h ^= qx & 0xff;
      h = Math.imul(h, 0x01000193);
      h ^= (qx >> 8) & 0xff;
      h = Math.imul(h, 0x01000193);
      h ^= (qx >> 16) & 0xff;
      h = Math.imul(h, 0x01000193);
      h ^= (qx >> 24) & 0xff;
      h = Math.imul(h, 0x01000193);

      h ^= qy & 0xff;
      h = Math.imul(h, 0x01000193);
      h ^= (qy >> 8) & 0xff;
      h = Math.imul(h, 0x01000193);
      h ^= (qy >> 16) & 0xff;
      h = Math.imul(h, 0x01000193);
      h ^= (qy >> 24) & 0xff;
      h = Math.imul(h, 0x01000193);
    }

    return { arr: buf, hash: h >>> 0 };
  }

  sameRoute(prevArr: Float64Array, currArr: Float64Array): boolean {
    const eps = 0.01;
    if (!prevArr || prevArr.length !== currArr.length) return false;
    // quick endpoint check first
    const L = currArr.length;
    if (Math.abs(prevArr[0] - currArr[0]) > eps) return false;
    if (Math.abs(prevArr[1] - currArr[1]) > eps) return false;
    if (Math.abs(prevArr[L - 2] - currArr[L - 2]) > eps) return false;
    if (Math.abs(prevArr[L - 1] - currArr[L - 1]) > eps) return false;
    // full compare
    for (let i = 0; i < L; i++) {
      if (Math.abs(prevArr[i] - currArr[i]) > eps) return false;
    }
    return true;
  }

  routeAll(forced = false): void {
    this.cachedRoutes.clear();
    this.graph.getElements().forEach((element) => {
      this.updateShape(element);
    });
    // RouteAll synchs the joints graph with the LibAvoid Graph.
    // This includes also removing obsolete stuff (e.g. because of loading another graph)
    const elementIds = this.graph.getElements().map((el) => el.id);
    Object.keys(this.shapeRefs)
      .filter((key) => !elementIds.includes(key))
      .forEach((id) => {
        this.deleteShape(id);
      });
    this.graph.getLinks().forEach((link) => this.updateConnector(link));
    const linkIds = this.graph.getLinks().map((l) => l.id);
    Object.keys(this.edgeRefs)
      .filter((key) => !linkIds.includes(key))
      .forEach((id) => {
        this.deleteConnector(id);
      });
    this.cachedRoutes.clear();
    this.triggerAvoidRouting(forced);
    this.edgeLabelPlacer.placeAllLabels();
  }

  /**
   * There is a compensation of a bug in the router (may it be the this class or the underlying avoid-lib).
   * There are two possible strategies for this
   * (a) just do nothing -> Link may look strange to the user, but he can compensate this by e.g. moving some nodes
   * (b) apply jointjs orthogonal router.
   *
   * I'd opt for (a) ... I'd only opt for (b) if the user cannot fix it. But he can always decide to use a jointjs router,
   * so "cannot fix it" should not happen.
   */
  resetLink(link: dia.Link): void {
    /* THIS IS THE CODE SECTION FOR OPTION (A) */
    // dothing
    /* THIS IS THE CODE SECTION FOR OPTION (B) */
    // const newAttributes = util.cloneDeep(link.attributes);
    // newAttributes.vertices = [];
    // newAttributes.router = {
    //   name: "orthogonal",
    // };
    // delete newAttributes.source.anchor;
    // delete newAttributes.target.anchor;
    // link.set(newAttributes, { avoidRouter: true });
  }

  onCellRemoved(cell: dia.Cell): void {
    if (cell.isElement()) {
      this.deleteShape(cell.id);
    } else {
      this.deleteConnector(cell.id);
    }
    this.triggerAvoidRouting();
  }

  onCellAdded(cell: dia.Cell): void {
    if (cell.isElement()) {
      this.updateShape(cell);
    } else {
      this.updateConnector(cell as dia.Link);
    }
    this.triggerAvoidRouting();
  }
  async triggerAvoidRouting(forced = false) {
    withRouter((Avoid, avoidRouter) => {
      if (forced) {
        // user clicked "Layout" Button
        this.edgeLabelPlacer.setActive(false);
        try {
          this.mode = "forced";
          avoidRouter.processTransaction();
        } finally {
          this.mode = "normal";
        }
        this.edgeLabelPlacer.setActive(true);
        this.edgeLabelPlacer.placeAllLabels();
        // Eager EdgeLabelPlacer feels pretty annoying
        this.edgeLabelPlacer.setActive(false);
      } else {
        avoidRouter.processTransaction();
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onCellChanged(cell: dia.Cell, opt: any): void {
    let needsRerouting = false;
    if ("position" in cell.changed || "size" in cell.changed) {
      if (!cell.isElement()) return;
      this.updateShape(cell);
      needsRerouting = true;
    } else if ("source" in cell.changed || "target" in cell.changed || "router" in cell.changed) {
      if (!cell.isLink()) return;
      if (!this.updateConnector(cell)) {
        this.resetLink(cell);
        return;
      }
      needsRerouting = true;
    }

    if (this.commitTransactions && needsRerouting) {
      this.triggerAvoidRouting();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onAvoidConnectorChange(connRefPtr: any): void {
    if (this.mode === "silenced") return;
    if (!layoutEnabled) {
      // honor the feature toggle
      return;
    }
    const link = this.linksByPointer[connRefPtr];
    if (!link) {
      return;
    }
    const router = link.get("router");
    if (this.mode === "forced" || !router) {
      this.routeLink(link);
    }
  }

  /**
   * I could produce an invalid route as follows:
   * - Create a statechart with two states "HIGH" and "LOW"
   * - move "LOW" to the bottom left corner of "HIGH" and keep some space for the transition
   * - add a self-transition to "LOW", give it the lib-avoid router --> should draw the link in the top right corner of LOW
   * - move "LOW" slowly towards "HIGH" --> will _sometime_ produce an invalid route.
   */
  isRouteValid(route: PolyLine): boolean {
    const size = route.size();
    if (size > 2) {
      return true;
    }
    const sourcePs = route.get_ps(0);
    const targetPs = route.get_ps(size - 1);
    if (sourcePs.x !== targetPs.x && sourcePs.y !== targetPs.y) {
      return false;
    }
    return true;
  }
}
